import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Card, CardContent } from '@werkstatt/ui';
import { LogOut, Monitor } from 'lucide-react';
import api from '../../lib/api';
import { readApiError } from '../../lib/apiError';
import type { SessionInfo } from '../../types';

export function SessionsTab() {
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => (await api.get<SessionInfo[]>('/auth/sessions')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sessions'] });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => {
      toast.success('Sitzung beendet');
      invalidate();
    },
    onError: (error: unknown) => toast.error(readApiError(error, 'Beenden fehlgeschlagen')),
  });

  const revokeAllOthers = async () => {
    const others = sessions.filter((s) => !s.is_current);
    await Promise.all(others.map((s) => api.delete(`/auth/sessions/${s.id}`)));
    toast.success('Alle anderen Sitzungen wurden beendet');
    invalidate();
  };

  return (
    <Card variant="glass">
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <p className="text-sm text-muted-foreground">
            Diese Geräte/Browser sind aktuell bei Ihrem Konto angemeldet.
          </p>
          {sessions.length > 1 && (
            <Button variant="outline" size="sm" onClick={revokeAllOthers}>
              Alle anderen abmelden
            </Button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground p-6">Wird geladen…</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">Keine aktiven Sitzungen.</p>
        ) : (
          <ul>
            {sessions.map((session) => (
              <li
                key={session.id}
                className={`flex items-center gap-3 px-6 py-4 border-b last:border-b-0
                  ${session.is_current ? 'bg-primary/5' : ''}`}
              >
                <Monitor className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">
                      {session.user_agent || 'Unbekanntes Gerät'}
                    </span>
                    {session.is_current && (
                      <Badge variant="outline" className="text-xs">Aktuelle Sitzung</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {session.ip_address ?? 'Unbekannte IP'}
                    {session.last_used_at &&
                      ` · zuletzt aktiv ${new Date(session.last_used_at).toLocaleString('de-DE')}`}
                  </p>
                </div>
                {!session.is_current && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => revokeMutation.mutate(session.id)}
                    aria-label="Sitzung beenden"
                  >
                    <LogOut className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
